import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Stack, TextField, Typography, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../api/client';
import type { PageResponse, VehicleDto } from '../api/types';
import { useAuthStore, hasAnyRole } from '../auth/store';

export function VehiclesPage() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const { user } = useAuthStore();
  const canEdit = hasAnyRole(user, 'ADMIN', 'OPERADOR');

  const { data, isLoading } = useQuery({
    queryKey: ['vehicles', q],
    queryFn: () =>
      api.get<PageResponse<VehicleDto>>('/vehicles', { params: { q: q || undefined, size: 100 } }).then((r) => r.data),
  });

  const cols: GridColDef<VehicleDto>[] = [
    { field: 'plateDisplay', headerName: 'Placa', flex: 1, renderCell: (p) => <Chip label={p.value} sx={{ fontFamily: 'monospace' }} size="small" /> },
    { field: 'driverName', headerName: 'Chofer', flex: 1.2, valueGetter: (_, r) => r.driver?.fullName ?? '' },
    { field: 'make', headerName: 'Marca', flex: 0.8 },
    { field: 'model', headerName: 'Modelo', flex: 0.8 },
    { field: 'year', headerName: 'Año', flex: 0.5 },
    { field: 'vehicleType', headerName: 'Tipo', flex: 0.8 },
    { field: 'status', headerName: 'Status', flex: 0.7,
      renderCell: (p) => <Chip size="small" label={p.value} color={p.value === 'ACTIVE' ? 'success' : 'default'} /> },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Camionetas</Typography>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav('/vehicles/new')}>
            Nueva camioneta
          </Button>
        )}
      </Stack>
      <TextField label="Buscar (placa)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ mb: 2 }} fullWidth />
      <Box sx={{ height: 600, bgcolor: 'background.paper' }}>
        <DataGrid<VehicleDto>
          rows={data?.content ?? []}
          columns={cols}
          loading={isLoading}
          getRowId={(r) => r.id}
          onRowClick={(p) => nav(`/vehicles/${p.row.id}`)}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Box>
    </Box>
  );
}
